import { useEffect, useState } from "react";

import { aspectFromSize } from "../utils/work";
import { vimeoThumbnailEndpoint } from "../utils/vimeo";
import type { WorkAspect } from "../types/work";

export type VimeoMeta = {
  title: string | null;
  thumbnail: string | null;
  aspect: WorkAspect | null;
};

const empty: VimeoMeta = { title: null, thumbnail: null, aspect: null };

/** Resolved metadata is cached per id, so each film is queried once per session. */
const cache = new Map<string, VimeoMeta>();
const inflight = new Map<string, Promise<void>>();

/**
 * Reads the real title, poster frame, and playback shape for a Vimeo film.
 * Returns nulls until it resolves, so callers can fall back to their own art
 * without a flash of empty frame.
 */
export function useVimeoMeta(id?: string): VimeoMeta {
  const [meta, setMeta] = useState<VimeoMeta>(() => (id ? cache.get(id) ?? empty : empty));

  useEffect(() => {
    if (!id) {
      setMeta(empty);
      return;
    }

    const cached = cache.get(id);
    if (cached) {
      setMeta(cached);
      return;
    }

    let active = true;

    const request =
      inflight.get(id) ??
      fetch(vimeoThumbnailEndpoint(id))
        .then((response) => (response.ok ? response.json() : Promise.reject(new Error("oembed failed"))))
        .then((data: { title?: string; thumbnail_url?: string; width?: number; height?: number }) => {
          cache.set(id, {
            title: data.title ?? null,
            // Vimeo suffixes a size onto the file name; asking for a wider crop keeps cards sharp.
            thumbnail: data.thumbnail_url ? data.thumbnail_url.replace(/-d_\d+x\d+$/, "-d_1280") : null,
            aspect: aspectFromSize(data.width, data.height),
          });
        })
        .catch(() => {
          cache.set(id, empty);
        })
        .finally(() => {
          inflight.delete(id);
        });

    inflight.set(id, request);
    void request.then(() => {
      if (active) setMeta(cache.get(id) ?? empty);
    });

    return () => {
      active = false;
    };
  }, [id]);

  return meta;
}
